import { Component, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';

import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from 'app/services/api.service';
import moment from 'moment-timezone';
import { FileExportService } from 'app/services/file-export.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CameraEvent, LockEvent } from 'app/models/report.model';
import { ReportService } from 'app/services/report.service';
import { ReportFilter } from 'app/services/helper.service';
import {
  ReportExportRowsService,
  ReportExportTable,
} from 'app/services/report-export-rows.service';
import { ReportPdfExportService } from 'app/services/report-pdf-export.service';
import { ReportAlertPdfDetailService } from 'app/services/report-alert-pdf-detail.service';
import {
  PDF_EXPORT_MAX_ROWS,
  PDF_EXPORT_WARN_ROWS,
  ALERT_CSV_FIELD_FILTER,
  ALERT_DETAIL_FIELD_FILTER,
  ALERT_EXPORT_HEADERS,
  CAMERA_EXPORT_HEADERS,
  LOCK_EXPORT_HEADERS,
  CAMERA_PDF_HEADER_KEYS,
  LOCK_PDF_HEADER_KEYS,
} from 'app/services/report-export.constants';

type AlertDownloadFieldSet = 'csv' | 'detail';

@Component({
    selector: 'app-download-panel',
    imports: [
    MatTooltipModule,
    MatExpansionModule,
    TranslateModule
],
    templateUrl: './download-panel.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    styleUrls: ['./download-panel.component.css']
})
export class DownloadPanelComponent implements OnDestroy {
  constructor(
    private translate: TranslateService,
    private api: ApiService,
    private reportService: ReportService,
    private fileExporter: FileExportService,
    private reportExportRows: ReportExportRowsService,
    private reportPdfExport: ReportPdfExportService,
    private reportAlertPdfDetail: ReportAlertPdfDetailService,
  ) {}

  PDFRequestPanelExpanded = false;

  loadingCSV = false;
  loadingPDF = false;
  loadingJSON = false;

  pdfError = false;

  @Input() PDFReportRequestFailed: boolean;
  @Output() PDFReportRequestFailedEvent = new EventEmitter<boolean>();

  @Input() limitExceed: boolean;
  @Output() limitExceedEvent = new EventEmitter<boolean>();

  @Input() alertLimitValue: string;
  @Output() alertLimitValueEvent = new EventEmitter<string>();

  @Input() filterStartDay: any;
  @Input() filterEndDay: any;
  @Input() filterStartTime: any;
  @Input() filterEndTime: any;
  @Input() advancedFilter: ReportFilter;
  @Input() weekDayFilter: any;
  @Input() customDeviceOption: any;
  @Input() customDeviceQuery: any;
  @Input() customBasestationOption: any;
  @Input() customBasestationQuery: any;
  @Input() cameraVisitsEnabled: boolean;
  @Input() selectedTab: any;

  @Input() user;

  private destroy$ = new Subject<void>();
  private pdfFailedAlertTimerId: ReturnType<typeof setTimeout> | null = null;

  downloadPdf(): void {
    this.resetPdfFailureState();
    this.loadingPDF = true;

    const exportPromise = this.runPdfExportForTab();

    exportPromise
      .then(() => {
        this.loadingPDF = false;
        this.PDFRequestPanelExpanded = false;
      })
      .catch((err: Error) => {
        this.loadingPDF = false;
        if (err?.message === 'PDF row limit exceeded' || err?.message === 'PDF export cancelled') {
          return;
        }
        this.pdfError = true;
        this.emitPdfFailed(false);
      });
  }

  private async runPdfExportForTab(): Promise<void> {
    switch (this.selectedTab) {
      case 'Alert':
        await this.exportAlertsPdf();
        break;
      case 'CameraVisit':
        await this.exportCameraEventsPdf();
        break;
      case 'LockEvent':
        await this.exportLockEventsPdf();
        break;
      default:
        throw new Error('Unsupported report tab for PDF export');
    }
  }

  private resetPdfFailureState(): void {
    this.clearPdfFailedAlertTimer();
    this.pdfError = false;
    this.limitExceed = false;
    this.limitExceedEvent.emit(false);
    this.PDFReportRequestFailed = false;
    this.PDFReportRequestFailedEvent.emit(false);
  }

  private async exportAlertsPdf(): Promise<void> {
    const alerts = await this.fetchAlertsForDownload('detail');
    const alertData = alerts ?? [];
    await this.exportPdfFromData(
      alertData,
      'alerts',
      null,
      this.translate.instant('NAV_REPORT') + ' — ' + this.translate.instant('ALERTS_TAB'),
      {
        content: this.reportAlertPdfDetail.buildAlertPdfContent(alertData),
        pageOrientation: 'landscape',
      }
    );
  }

  private async exportCameraEventsPdf(): Promise<void> {
    const events = await this.fetchCameraVisitsForDownload();
    const table = this.withTranslatedHeaders(
      this.reportExportRows.getCameraVisitExportTable(events ?? []),
      CAMERA_PDF_HEADER_KEYS
    );
    await this.exportPdfFromData(
      events ?? [],
      'camera-events',
      table,
      this.translate.instant('NAV_REPORT') + ' — ' + this.translate.instant('CAMERA_EVENTS')
    );
  }

  private async exportLockEventsPdf(): Promise<void> {
    const events = await this.fetchLockingEventsForDownload();
    const table = this.withTranslatedHeaders(
      this.reportExportRows.getLockEventExportTable(events ?? []),
      LOCK_PDF_HEADER_KEYS
    );
    await this.exportPdfFromData(
      events ?? [],
      'lock-events',
      table,
      this.translate.instant('NAV_REPORT') + ' — ' + this.translate.instant('LOCK_EVENTS')
    );
  }

  private withTranslatedHeaders(
    table: ReportExportTable,
    headerKeys: readonly string[]
  ): ReportExportTable {
    return {
      ...table,
      headers: headerKeys.map((key) => this.translate.instant(key)),
    };
  }

  private async exportPdfFromData(
    data: unknown[],
    filenamePrefix: string,
    table: ReturnType<ReportExportRowsService['getAlertExportTable']> | null,
    title: string,
    pdfOptions?: {
      content?: import('pdfmake/interfaces').Content[];
      pageOrientation?: 'portrait' | 'landscape';
    }
  ): Promise<void> {
    const rowCount = data.length;
    if (rowCount > PDF_EXPORT_MAX_ROWS) {
      this.emitPdfFailed(true, String(PDF_EXPORT_MAX_ROWS));
      throw new Error('PDF row limit exceeded');
    }
    if (rowCount > PDF_EXPORT_WARN_ROWS) {
      const confirmMsg = this.translate.instant('PDF_EXPORT_LARGE_CONFIRM', {
        count: rowCount,
      });
      if (!window.confirm(confirmMsg)) {
        throw new Error('PDF export cancelled');
      }
    }

    const filename = `${filenamePrefix}-${moment().format('YYYY-MM-DD')}`;
    const subtitle = this.buildDateRangeSubtitle();

    await this.reportPdfExport.exportTable({
      title,
      subtitle,
      filename,
      table: table ?? undefined,
      content: pdfOptions?.content,
      pageOrientation: pdfOptions?.pageOrientation,
    });
  }

  private buildDateRangeSubtitle(): string | undefined {
    const start = moment(this.filterStartDay);
    const end = moment(this.filterEndDay);
    if (!start.isValid() || !end.isValid()) {
      return undefined;
    }
    return `${start.format('L')} – ${end.format('L')}`;
  }

  private emitPdfFailed(limitExceed: boolean, limitValue?: string): void {
    this.clearPdfFailedAlertTimer();
    this.limitExceed = limitExceed;
    this.limitExceedEvent.emit(this.limitExceed);
    if (limitValue !== undefined) {
      this.alertLimitValue = limitValue;
      this.alertLimitValueEvent.emit(this.alertLimitValue);
    }
    this.PDFReportRequestFailed = true;
    this.PDFReportRequestFailedEvent.emit(true);
    this.pdfFailedAlertTimerId = setTimeout(() => {
      this.PDFReportRequestFailed = false;
      this.PDFReportRequestFailedEvent.emit(false);
      this.limitExceed = false;
      this.limitExceedEvent.emit(false);
      this.pdfFailedAlertTimerId = null;
    }, 7000);
  }

  private clearPdfFailedAlertTimer(): void {
    if (this.pdfFailedAlertTimerId !== null) {
      clearTimeout(this.pdfFailedAlertTimerId);
      this.pdfFailedAlertTimerId = null;
    }
  }

  downloadCsvOrJson(exportType: 'csv' | 'json'): void {
    switch (this.selectedTab) {
      case 'Alert':
        this.exportAlertsToCsvOrJson(exportType);
        break;
      case 'CameraVisit':
        this.exportCameraEventsToCsv();
        break;
      case 'LockEvent':
        this.exportLockEventsToCsv();
        break;
    }
  }

  exportAlertsToCsvOrJson(exportType: 'csv' | 'json'): void {
    const filename = 'alerts-' + moment().format('YYYY-MM-DD');
    const options = {
      fieldSeparator: ';',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: false,
      headers: [...ALERT_EXPORT_HEADERS],
    };

    if (exportType === 'csv') {
      this.loadingCSV = true;
    }
    if (exportType === 'json') {
      this.loadingJSON = true;
    }

    const fieldSet: AlertDownloadFieldSet = exportType === 'csv' ? 'csv' : 'detail';
    this.fetchAlertsForDownload(fieldSet)
      .then((alerts) => {
        if (alerts) {
          const exportData = alerts ?? [];
          switch (exportType) {
            case 'csv':
              this.fileExporter.exportAlertsToCSV(exportData, filename, options);
              break;
            case 'json':
              this.fileExporter.exportAlertsToJson(exportData, filename);
              break;
          }
          this.PDFRequestPanelExpanded = false;
        }
      })
      .catch((err) => {
        console.error('Alert export failed', err);
      })
      .finally(() => {
        if (exportType === 'csv') {
          this.loadingCSV = false;
        }
        if (exportType === 'json') {
          this.loadingJSON = false;
        }
      });
  }

  fetchAlertsForDownload(fieldSet: AlertDownloadFieldSet = 'detail'): Promise<any[]> {
    const fieldFilter =
      fieldSet === 'csv' ? [...ALERT_CSV_FIELD_FILTER] : [...ALERT_DETAIL_FIELD_FILTER];

    return new Promise((resolve, reject) => {
      let alertsData: any = {
        paging: {
          alertsPerPage: 0,
          pageNumber: 1,
        },
        fieldFilter,
      };
      alertsData = this.reportService.setReportFilters(
        alertsData,
        this.advancedFilter,
        this.customDeviceOption,
        this.customDeviceQuery,
        this.customBasestationOption,
        this.customBasestationQuery,
        this.weekDayFilter,
        this.filterStartDay,
        this.filterEndDay,
        this.filterStartTime,
        this.filterEndTime
      );
      alertsData = this.reportService.setAlertFallbackFilter(alertsData, this.advancedFilter);
      this.api
        .getAlertHistory(alertsData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => resolve(res),
          error: (err) => reject(err),
        });
    });
  }

  exportCameraEventsToCsv(): void {
    const filename = 'camera-events-' + moment().format('YYYY-MM-DD');
    const options = {
      fieldSeparator: ';',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: false,
      headers: [...CAMERA_EXPORT_HEADERS],
    };

    this.loadingCSV = true;

    this.fetchCameraVisitsForDownload()
      .then((events) => {
        if (events) {
          const exportData = events ?? [];
          this.fileExporter.exportCameraEventsToCSV(exportData, filename, options);
          this.PDFRequestPanelExpanded = false;
        }
      })
      .catch((err) => {
        console.error('Camera event export failed', err);
      })
      .finally(() => {
        this.loadingCSV = false;
      });
  }

  fetchCameraVisitsForDownload(): Promise<CameraEvent[]> {
    return new Promise((resolve, reject) => {
      const page = 1;
      const pageSize = 0;
      this.reportService
        .getCameraVisit(
          page,
          pageSize,
          this.advancedFilter,
          this.customDeviceOption,
          this.customDeviceQuery,
          this.customBasestationOption,
          this.customBasestationQuery,
          this.weekDayFilter,
          this.filterStartDay,
          this.filterEndDay,
          this.filterStartTime,
          this.filterEndTime,
          this.cameraVisitsEnabled
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => resolve(res),
          error: (err) => reject(err),
        });
    });
  }

  exportLockEventsToCsv(): void {
    const filename = 'lock-events-' + moment().format('YYYY-MM-DD');
    const options = {
      fieldSeparator: ';',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: false,
      headers: [...LOCK_EXPORT_HEADERS],
    };

    this.loadingCSV = true;

    this.fetchLockingEventsForDownload()
      .then((events) => {
        if (events) {
          this.fileExporter.exportLockEventsToCSV(events, filename, options);
          this.PDFRequestPanelExpanded = false;
        }
      })
      .catch((err) => {
        console.error('Lock event export failed', err);
      })
      .finally(() => {
        this.loadingCSV = false;
      });
  }

  fetchLockingEventsForDownload(): Promise<LockEvent[]> {
    return new Promise((resolve, reject) => {
      const page = 1;
      const pageSize = 0;
      this.reportService
        .getLockingEvents(
          page,
          pageSize,
          this.advancedFilter,
          this.customDeviceOption,
          this.customDeviceQuery,
          this.customBasestationOption,
          this.customBasestationQuery,
          this.weekDayFilter,
          this.filterStartDay,
          this.filterEndDay,
          this.filterStartTime,
          this.filterEndTime
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => resolve(res),
          error: (err) => reject(err),
        });
    });
  }

  ngOnDestroy(): void {
    this.clearPdfFailedAlertTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
